import { confirmAsk, notify } from '../../store/feedback';
import { isAbsent } from '../../utils/absences';
import { dateFromIso } from '../../utils/dates';
import { fmtDate } from '../../utils/format';
import {
  devAssignedHoursOnDay,
  findWorkedDay,
  lastWorkedDay,
  maxHoursPerDay,
  nonWorkedWeekdays,
} from '../../utils/planning';

const WEEKDAY_NAMES = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const p2 = (n) => String(n).padStart(2, '0');

// Fin ramenée au dernier jour travaillé si la plage contient des jours que le membre ne travaille
// pas (avec confirmation). undefined = annulé ou impossible (alerte déjà affichée).
async function clampToWorkedDays(dev, debut, fin, devWorkDays) {
  const problemDays = nonWorkedWeekdays(debut, fin, devWorkDays);
  if (problemDays.size === 0) return fin;
  const dayList = [...problemDays]
    .sort()
    .map((d) => WEEKDAY_NAMES[d])
    .join(' et ');
  const lastValid = lastWorkedDay(debut, fin, devWorkDays);
  if (!lastValid)
    return void notify(`${dev?.nom || 'Ce dev'} n'a aucun jour travaillé dans cette plage.`);
  const lv = dateFromIso(lastValid);
  const date = `${p2(lv.getDate())}/${p2(lv.getMonth() + 1)}/${lv.getFullYear()}`;
  const ok = await confirmAsk(
    `${dev?.nom || 'Ce dev'} ne travaille pas le ${dayList}.\nAjuster la fin au ${date} et enregistrer ?`,
    { title: 'Jours non travaillés', confirmLabel: 'Ajuster et enregistrer' },
  );
  return ok ? lastValid : undefined;
}

// Fin ramenée à la fin de contrat (avec confirmation) ; refus si la période commence après.
async function clampToContract(dev, debut, fin) {
  if (!dev?.dateFinContrat) return fin;
  const contractEnd = fmtDate(dev.dateFinContrat);
  if (debut > dev.dateFinContrat)
    return void notify(
      `Impossible : le contrat de ${dev?.nom || 'ce collaborateur'} est terminé depuis le ${contractEnd}.`,
    );
  if (fin <= dev.dateFinContrat) return fin;
  const ok = await confirmAsk(
    `${dev?.nom || 'Ce collaborateur'} termine son contrat le ${contractEnd}.\nAjuster la fin et enregistrer ?`,
    { title: 'Fin de contrat', confirmLabel: 'Ajuster et enregistrer' },
  );
  return ok ? dev.dateFinContrat : undefined;
}

export default function planningValidationHandlers() {
  // Version interactive de la même validation que tryCreateAff (clamp jours travaillés / fin de
  // contrat avec confirmation, blocage dur sur absence/dépassement de capacité) — utilisée par la
  // création et l'édition d'une affectation SIMPLE (un seul dev, confirmations demandées à
  // l'utilisateur), par opposition à tryCreateAff qui est silencieuse (pensée pour l'affectation
  // groupée multi-dev, où demander une confirmation par personne serait impraticable).
  // Retourne la date de fin effective (éventuellement clampée) en cas de succès, ou null si
  // l'utilisateur a annulé / si un blocage dur a été signalé (alert déjà affichée par la fonction).
  async function validateAssignmentInteractive(
    dev,
    devId,
    debut,
    fin,
    devAbs,
    newH,
    assignmentsForCapacity,
  ) {
    const devWorkDays = dev?.joursTravailles || [];
    const maxH = maxHoursPerDay(dev);
    let effectiveFin =
      devWorkDays.length > 0 ? await clampToWorkedDays(dev, debut, fin, devWorkDays) : fin;
    if (effectiveFin === undefined) return null;
    effectiveFin = await clampToContract(dev, debut, effectiveFin);
    if (effectiveFin === undefined) return null;
    const blocage = findWorkedDay(debut, effectiveFin, devWorkDays, (iso) => {
      if (isAbsent(devAbs, iso))
        return 'Impossible : ce collaborateur est absent sur certaines journées de cette période.';
      if (devAssignedHoursOnDay(assignmentsForCapacity, devId, iso) + newH > maxH + 0.001)
        return `Impossible : le total des affectations dépasserait ${maxH}h/j sur certaines journées.`;
      return undefined;
    });
    if (blocage) {
      notify(blocage);
      return null;
    }
    return effectiveFin;
  }

  return { validateAssignmentInteractive };
}
